/**
 * NEUNEXUS Health · Renalix — dedicated landing page for the Health division.
 *
 * Content is grounded in the NeuNexus Health Campus strategic document (Uetze,
 * Lower Saxony). Anything not yet operational is explicitly framed as
 * *strategic vision* / *conceptual architecture*.
 *
 * Full DE / EN / PT via shared LangCtx.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ChevronRight, Brain, HeartPulse, Activity, Layers,
  ShieldCheck, Lock, Database, Cloud, Workflow, Building2, Sparkles,
  CheckCircle2, Cpu, LineChart, Stethoscope, FileText, Download,
} from "lucide-react";
import { LangCtx, LanguageSwitcher } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  rose:    "#F43F5E",
  teal:    "#14B8A6",
  navy:    "#1E3A8A",
  green:   "#22C55E",
  amber:   "#F59E0B",
  blue:    "#3B82F6",
  purple:  "#A855F7",
  cyan:    "#06B6D4",
};

// -------------------------------------------------------- i18n dictionary

const DATA = {
  en: {
    docTitle: "Renalix · NeuNexus Health Intelligence · BSGOTECH",
    reportsLink: "All Executive Reports",
    eyebrowDivision: "NEUNEXUS Health · Renalix",
    location: "Uetze Health Innovation Campus · Lower Saxony · Germany",
    heroTitleA: "NeuNexus Health ",
    heroTitleB: "Intelligence",
    heroLead: "Building the predictive healthcare infrastructure of tomorrow.",
    heroSub: "A converged infrastructure for predictive renal care — connecting Pflege facilities, hospitals and research through a single European AI intelligence layer.",
    pillars: [
      { title: "German engineering",   desc: "Precision foundation for digital health." },
      { title: "Sustainable infra.",   desc: "Built for long-term operational resilience." },
      { title: "AI-powered care",      desc: "Epicenter of regional health-tech innovation." },
      { title: "Integrated ecosystem", desc: "Close to Hannover's medical hubs." },
    ],
    phygital: {
      eyebrow: "The Uetze Health Innovation Campus",
      title: "The 'Phygital' Architecture — where bits meet bricks.",
      sub: "The Uetze Campus is deliberately designed to mirror our digital ecosystem. By unifying physical zones with cloud layers, we make invisible predictive AI tangible, observable and scalable for clinical partners.",
      items: [
        { title: "Physical blueprint", desc: "Campus zones mapped to platform capabilities — one physical footprint per operational role." },
        { title: "Digital reality",    desc: "Cloud layers, edge nodes and AI models rendering the invisible tangible." },
        { title: "One intelligence layer, two platforms", desc: "A single AI core powering Renalix Care (Pflege) and Renalix Pro (Hospitals)." },
      ],
    },
    aiCore: {
      eyebrow: "The Intelligence Hub",
      title: "The AI core engine behind predictive renal care.",
      sub: "Six converged capabilities inside a single European AI core — engineered to intercept risk before it becomes crisis.",
      items: [
        { title: "Digital Kidney Twin",       desc: "Dynamic simulation of renal function per patient." },
        { title: "Predictive AI models",      desc: "Early risk detection and prognosis at population and individual scale." },
        { title: "Biomarker analytics",       desc: "Advanced processing of biological data and novel biomarkers." },
        { title: "Clinical data integration", desc: "Real-time integration of EHR, medical devices and care data." },
        { title: "Continuous learning",       desc: "AI models improving with real-world clinical data." },
        { title: "Clinical decision support", desc: "Intelligent insights delivered where the decision happens." },
      ],
    },
    zones: {
      eyebrow: "The Ecosystem Synthesis",
      title: "One intelligence layer. Two operational platforms.",
      sub: "The same AI core powers both community-facing preventive intelligence and acute-care clinical intelligence — the loop closes when a Pflege signal triggers a hospital response.",
      zoneLabel: "Campus Zone",
      care: {
        name: "Renalix Care",
        tagline: "Preventive intelligence for Pflege & hospices",
        desc: "Human-centric intelligence designed to intercept risks before they escalate to acute events.",
        bullets: ["Hydration monitoring", "Weight tracking", "Edema detection", "Comfort & symptom intelligence", "AI risk alerts to caregivers", "Continuous vitals capture"],
      },
      pro: {
        name: "Renalix Pro",
        tagline: "Clinical intelligence for hospitals",
        desc: "Specialist-grade decision support for renal teams — AKI prediction, precision medicine and closed-loop response.",
        bullets: ["AKI (Acute Kidney Injury) prediction", "eGFR & trend analysis", "Precision medicine cohorts", "Clinical decision support", "EHR / FHIR integration", "Real-time risk dashboards"],
      },
    },
    loop: {
      eyebrow: "The Predictive Loop in Action",
      title: "From a vital sign in a Pflege room to a preventive protocol in the ward.",
      sub: "Five verifiable steps — instrumented, auditable, and closed inside the same intelligence layer.",
      steps: [
        { title: "Capture",      desc: "Vitals logged in a rural Pflege facility (Renalix Care)." },
        { title: "Transmission", desc: "Data flows into the Secure Medical Cloud via FHIR." },
        { title: "Processing",   desc: "NeuNexus AI Core updates the patient's Digital Kidney Twin." },
        { title: "Insight",      desc: "An AKI risk alert is generated and sent to a specialist's dashboard (Renalix Pro)." },
        { title: "Intervention", desc: "Hospital initiates preventive protocol, closing the loop." },
      ],
    },
    compliance: {
      eyebrow: "Secure Healthcare Infrastructure",
      title: "Sovereign, encrypted, audit-ready — by design.",
      sub: "The backbone of digital intelligence: European hosting, end-to-end encryption and regulatory armour that is not a checkbox but an architecture decision.",
      items: [
        { title: "Secure Medical Cloud", desc: "High-availability infrastructure for mission-critical healthcare operations." },
        { title: "Encrypted data",       desc: "End-to-end security — patient data impenetrable in transit and at rest." },
        { title: "Sovereign hosting",    desc: "Guaranteed German hosting — gold standard for European operations." },
        { title: "GDPR / DSGVO",         desc: "Complete data sovereignty and strict adherence to European privacy laws." },
        { title: "FHIR integration",     desc: "Seamless standardised interoperability with existing hospital EHR." },
        { title: "EU MDR ready",         desc: "Architected from day one for software as a medical device under EU MDR." },
      ],
    },
    roadmap: {
      eyebrow: "Strategic Roadmap",
      title: "Uetze project highlights — 2024 and beyond.",
      sub: "Five concrete engineering horizons — each a milestone, not a headline.",
      items: [
        { year: "2024",  title: "Foundation",             desc: "Infrastructure & AI Core established." },
        { year: "2025",  title: "Pilot Validation",       desc: "Clinical partnerships, Renalix Experience Center build phase, sustainable energy system." },
        { year: "2026",  title: "AI Digital Twin",        desc: "Full model activation of the Digital Kidney Twin." },
        { year: "2027",  title: "Medical Certification",  desc: "EU MDR compliance achieved." },
        { year: "2028+", title: "Commercial Scaling",     desc: "European market expansion." },
      ],
    },
    closing: {
      eyebrow: "From Digital Intelligence to Everyday Health",
      title: "Uetze is not a headquarters — it is the foundational infrastructure for predictive renal healthcare across Europe.",
      ctaReport: "Read the strategic report",
      ctaBack: "Back to BSGOTECH",
      ctaPdf: "Health Campus (PDF · 18.4 MB)",
      ctaResearch: "Research Blueprint (PDF · 17 MB)",
      legalLine: "NeuNexus Health Intelligence GmbH · Uetze · Lower Saxony · Germany",
    },
  },
  de: {
    docTitle: "Renalix · NeuNexus Health Intelligence · BSGOTECH",
    reportsLink: "Alle Executive Berichte",
    eyebrowDivision: "NEUNEXUS Health · Renalix",
    location: "Uetze Health Innovation Campus · Niedersachsen · Deutschland",
    heroTitleA: "NeuNexus Health ",
    heroTitleB: "Intelligence",
    heroLead: "Wir bauen die prädiktive Gesundheitsinfrastruktur von morgen.",
    heroSub: "Eine konvergente Infrastruktur für prädiktive Nephrologie — sie verbindet Pflegeeinrichtungen, Kliniken und Forschung über eine gemeinsame europäische KI-Intelligenzschicht.",
    pillars: [
      { title: "Deutsche Ingenieurskunst", desc: "Präzisionsfundament für Digital Health." },
      { title: "Nachhaltige Infrastruktur", desc: "Für langfristige operative Resilienz gebaut." },
      { title: "KI-gestützte Versorgung",   desc: "Epizentrum regionaler Health-Tech-Innovation." },
      { title: "Integriertes Ökosystem",    desc: "In der Nähe der medizinischen Zentren Hannovers." },
    ],
    phygital: {
      eyebrow: "Der Uetze Health Innovation Campus",
      title: "Die 'Phygital'-Architektur — wo Bits auf Steine treffen.",
      sub: "Der Uetzer Campus spiegelt bewusst unser digitales Ökosystem wider. Durch die Verbindung physischer Zonen mit Cloud-Schichten machen wir unsichtbare prädiktive KI greifbar, beobachtbar und für klinische Partner skalierbar.",
      items: [
        { title: "Physischer Blueprint", desc: "Campuszonen sind auf Plattformfähigkeiten abgebildet — ein physischer Fußabdruck pro operativer Rolle." },
        { title: "Digitale Realität",    desc: "Cloud-Schichten, Edge-Knoten und KI-Modelle machen das Unsichtbare greifbar." },
        { title: "Eine Intelligenzschicht, zwei Plattformen", desc: "Ein einziger KI-Kern für Renalix Care (Pflege) und Renalix Pro (Kliniken)." },
      ],
    },
    aiCore: {
      eyebrow: "Das Intelligenz-Hub",
      title: "Die KI-Kernmaschine hinter prädiktiver Nephrologie.",
      sub: "Sechs konvergente Fähigkeiten in einem einzigen europäischen KI-Kern — konzipiert, um Risiken abzufangen, bevor sie zur Krise werden.",
      items: [
        { title: "Digitaler Nieren-Zwilling",  desc: "Dynamische Simulation der Nierenfunktion pro Patient." },
        { title: "Prädiktive KI-Modelle",      desc: "Frühe Risikoerkennung und Prognose auf Populations- und Einzelebene." },
        { title: "Biomarker-Analytik",         desc: "Erweiterte Verarbeitung biologischer Daten und neuartiger Biomarker." },
        { title: "Klinische Datenintegration", desc: "Echtzeit-Integration von EHR, Medizingeräten und Pflegedaten." },
        { title: "Kontinuierliches Lernen",    desc: "KI-Modelle, die sich mit realen klinischen Daten verbessern." },
        { title: "Klinische Entscheidungshilfe", desc: "Intelligente Erkenntnisse dort, wo die Entscheidung fällt." },
      ],
    },
    zones: {
      eyebrow: "Die Ökosystem-Synthese",
      title: "Eine Intelligenzschicht. Zwei operative Plattformen.",
      sub: "Derselbe KI-Kern treibt sowohl gemeindenahe präventive Intelligenz als auch akutklinische Intelligenz an — der Kreislauf schließt sich, wenn ein Pflege-Signal eine Krankenhausreaktion auslöst.",
      zoneLabel: "Campus-Zone",
      care: {
        name: "Renalix Care",
        tagline: "Präventive Intelligenz für Pflege & Hospize",
        desc: "Menschenzentrierte Intelligenz, die Risiken abfängt, bevor sie zu akuten Ereignissen eskalieren.",
        bullets: ["Hydratations-Monitoring", "Gewichtsverlauf", "Ödem-Erkennung", "Komfort- & Symptom-Intelligenz", "KI-Risikowarnungen an Pflegekräfte", "Kontinuierliche Vitaldaten-Erfassung"],
      },
      pro: {
        name: "Renalix Pro",
        tagline: "Klinische Intelligenz für Krankenhäuser",
        desc: "Fachärztliche Entscheidungsunterstützung für nephrologische Teams — AKI-Prädiktion, Präzisionsmedizin und Closed-Loop-Reaktion.",
        bullets: ["AKI-Prädiktion (Akute Nierenverletzung)", "eGFR- & Trendanalyse", "Präzisionsmedizin-Kohorten", "Klinische Entscheidungshilfe", "EHR- / FHIR-Integration", "Echtzeit-Risiko-Dashboards"],
      },
    },
    loop: {
      eyebrow: "Der prädiktive Kreislauf in Aktion",
      title: "Vom Vitalzeichen im Pflegezimmer zum präventiven Protokoll auf der Station.",
      sub: "Fünf überprüfbare Schritte — instrumentiert, prüffähig und in derselben Intelligenzschicht geschlossen.",
      steps: [
        { title: "Erfassung",     desc: "Vitalwerte werden in einer ländlichen Pflegeeinrichtung protokolliert (Renalix Care)." },
        { title: "Übertragung",   desc: "Daten fließen über FHIR in die Secure Medical Cloud." },
        { title: "Verarbeitung",  desc: "Der NeuNexus KI-Kern aktualisiert den digitalen Nieren-Zwilling des Patienten." },
        { title: "Erkenntnis",    desc: "Ein AKI-Risiko-Alarm wird erzeugt und an das Dashboard eines Facharztes (Renalix Pro) gesendet." },
        { title: "Intervention",  desc: "Das Krankenhaus startet ein präventives Protokoll — der Kreislauf schließt sich." },
      ],
    },
    compliance: {
      eyebrow: "Sichere Gesundheitsinfrastruktur",
      title: "Souverän, verschlüsselt, prüffähig — by design.",
      sub: "Das Rückgrat digitaler Intelligenz: europäisches Hosting, Ende-zu-Ende-Verschlüsselung und regulatorische Panzerung, die keine Checkbox, sondern eine Architekturentscheidung ist.",
      items: [
        { title: "Secure Medical Cloud", desc: "Hochverfügbare Infrastruktur für missionskritische Healthcare-Operationen." },
        { title: "Verschlüsselte Daten", desc: "Ende-zu-Ende-Sicherheit — Patientendaten in Ruhe und Transit unantastbar." },
        { title: "Souveränes Hosting",   desc: "Garantiertes Hosting in Deutschland — Goldstandard für europäische Betriebe." },
        { title: "GDPR / DSGVO",         desc: "Vollständige Datensouveränität und strikte Einhaltung europäischer Datenschutzgesetze." },
        { title: "FHIR-Integration",     desc: "Nahtlose standardisierte Interoperabilität mit bestehenden Krankenhaus-EHR." },
        { title: "EU MDR ready",         desc: "Von Tag eins an als Software as a Medical Device unter EU MDR konzipiert." },
      ],
    },
    roadmap: {
      eyebrow: "Strategische Roadmap",
      title: "Uetze-Projekt-Highlights — 2024 und darüber hinaus.",
      sub: "Fünf konkrete Engineering-Horizonte — jeder ein Meilenstein, keine Schlagzeile.",
      items: [
        { year: "2024",  title: "Grundlage",              desc: "Infrastruktur & KI-Kern etabliert." },
        { year: "2025",  title: "Pilot-Validierung",      desc: "Klinische Partnerschaften, Aufbauphase des Renalix Experience Centers, nachhaltiges Energiesystem." },
        { year: "2026",  title: "KI Digital Twin",        desc: "Vollständige Modell-Aktivierung des digitalen Nieren-Zwillings." },
        { year: "2027",  title: "Medizinische Zertifizierung", desc: "EU-MDR-Konformität erreicht." },
        { year: "2028+", title: "Kommerzielle Skalierung", desc: "Europäische Markterweiterung." },
      ],
    },
    closing: {
      eyebrow: "Von digitaler Intelligenz zu alltäglicher Gesundheit",
      title: "Uetze ist kein Hauptquartier — es ist die grundlegende Infrastruktur für prädiktive Nephrologie in ganz Europa.",
      ctaReport: "Strategiebericht lesen",
      ctaBack: "Zurück zu BSGOTECH",
      ctaPdf: "Health Campus (PDF · 18,4 MB)",
      ctaResearch: "Research Blueprint (PDF · 17 MB)",
      legalLine: "NeuNexus Health Intelligence GmbH · Uetze · Niedersachsen · Deutschland",
    },
  },
  pt: {
    docTitle: "Renalix · NeuNexus Health Intelligence · BSGOTECH",
    reportsLink: "Todos os Relatórios Executivos",
    eyebrowDivision: "NEUNEXUS Health · Renalix",
    location: "Uetze Health Innovation Campus · Baixa Saxónia · Alemanha",
    heroTitleA: "NeuNexus Health ",
    heroTitleB: "Intelligence",
    heroLead: "A construir a infraestrutura de saúde preditiva de amanhã.",
    heroSub: "Uma infraestrutura convergente para cuidado renal preditivo — a ligar unidades de Pflege, hospitais e investigação através de uma única camada europeia de inteligência artificial.",
    pillars: [
      { title: "Engenharia alemã",       desc: "Fundação de precisão para saúde digital." },
      { title: "Infraestrutura sustentável", desc: "Construída para resiliência operacional de longo prazo." },
      { title: "Cuidado potenciado por IA",  desc: "Epicentro da inovação regional em health-tech." },
      { title: "Ecossistema integrado",  desc: "Próximo dos polos médicos de Hannover." },
    ],
    phygital: {
      eyebrow: "O Campus Uetze de Inovação em Saúde",
      title: "A arquitectura 'Phygital' — onde os bits encontram os tijolos.",
      sub: "O Campus de Uetze foi deliberadamente concebido para espelhar o nosso ecossistema digital. Ao unificar zonas físicas com camadas na cloud, tornamos a IA preditiva invisível tangível, observável e escalável para parceiros clínicos.",
      items: [
        { title: "Blueprint físico",       desc: "Zonas do campus mapeadas às capacidades da plataforma — uma pegada física por função operacional." },
        { title: "Realidade digital",      desc: "Camadas na cloud, nós edge e modelos de IA tornam o invisível tangível." },
        { title: "Uma camada de inteligência, duas plataformas", desc: "Um único núcleo de IA potencia Renalix Care (Pflege) e Renalix Pro (Hospitais)." },
      ],
    },
    aiCore: {
      eyebrow: "O Hub de Inteligência",
      title: "O motor central de IA por trás do cuidado renal preditivo.",
      sub: "Seis capacidades convergentes num único núcleo europeu de IA — desenhado para interceptar risco antes de se tornar crise.",
      items: [
        { title: "Gémeo Digital Renal",     desc: "Simulação dinâmica da função renal por paciente." },
        { title: "Modelos de IA preditiva", desc: "Detecção precoce de risco e prognóstico à escala populacional e individual." },
        { title: "Análise de biomarcadores", desc: "Processamento avançado de dados biológicos e novos biomarcadores." },
        { title: "Integração de dados clínicos", desc: "Integração em tempo real de EHR, dispositivos médicos e dados de cuidado." },
        { title: "Aprendizagem contínua",   desc: "Modelos de IA que evoluem com dados clínicos do mundo real." },
        { title: "Apoio à decisão clínica", desc: "Insights inteligentes entregues onde a decisão acontece." },
      ],
    },
    zones: {
      eyebrow: "A Síntese do Ecossistema",
      title: "Uma camada de inteligência. Duas plataformas operacionais.",
      sub: "O mesmo núcleo de IA alimenta tanto a inteligência preventiva orientada à comunidade como a inteligência clínica de cuidado agudo — o ciclo fecha-se quando um sinal em Pflege desencadeia uma resposta hospitalar.",
      zoneLabel: "Zona do Campus",
      care: {
        name: "Renalix Care",
        tagline: "Inteligência preventiva para Pflege e cuidados paliativos",
        desc: "Inteligência centrada no ser humano, desenhada para interceptar riscos antes que escalem para eventos agudos.",
        bullets: ["Monitorização de hidratação", "Acompanhamento de peso", "Detecção de edema", "Inteligência de conforto e sintomas", "Alertas de risco por IA a cuidadores", "Captura contínua de sinais vitais"],
      },
      pro: {
        name: "Renalix Pro",
        tagline: "Inteligência clínica para hospitais",
        desc: "Apoio à decisão de nível especialista para equipas de nefrologia — predição de LRA, medicina de precisão e resposta em ciclo fechado.",
        bullets: ["Predição de LRA (Lesão Renal Aguda)", "Análise de eGFR e tendências", "Coortes de medicina de precisão", "Apoio à decisão clínica", "Integração EHR / FHIR", "Dashboards de risco em tempo real"],
      },
    },
    loop: {
      eyebrow: "O Ciclo Preditivo em Acção",
      title: "De um sinal vital numa unidade de Pflege a um protocolo preventivo na enfermaria.",
      sub: "Cinco passos verificáveis — instrumentados, auditáveis e fechados dentro da mesma camada de inteligência.",
      steps: [
        { title: "Captura",       desc: "Sinais vitais registados numa unidade rural de Pflege (Renalix Care)." },
        { title: "Transmissão",   desc: "Dados fluem para a Secure Medical Cloud via FHIR." },
        { title: "Processamento", desc: "O núcleo de IA NeuNexus actualiza o Gémeo Digital Renal do paciente." },
        { title: "Insight",       desc: "Um alerta de risco de LRA é gerado e enviado ao dashboard do especialista (Renalix Pro)." },
        { title: "Intervenção",   desc: "O hospital inicia protocolo preventivo, fechando o ciclo." },
      ],
    },
    compliance: {
      eyebrow: "Infraestrutura de Saúde Segura",
      title: "Soberana, encriptada, auditável — by design.",
      sub: "A espinha dorsal da inteligência digital: hospedagem europeia, encriptação ponto-a-ponto e blindagem regulatória que não é uma caixa a marcar mas sim uma decisão de arquitectura.",
      items: [
        { title: "Secure Medical Cloud",  desc: "Infraestrutura de alta disponibilidade para operações críticas em saúde." },
        { title: "Dados encriptados",     desc: "Segurança ponto-a-ponto — dados do paciente inatacáveis em trânsito e em repouso." },
        { title: "Hospedagem soberana",   desc: "Hospedagem garantida na Alemanha — padrão-ouro para operações europeias." },
        { title: "GDPR / DSGVO",          desc: "Total soberania de dados e cumprimento estrito das leis europeias de privacidade." },
        { title: "Integração FHIR",       desc: "Interoperabilidade padronizada sem atrito com o EHR hospitalar existente." },
        { title: "Pronto para EU MDR",    desc: "Arquitectado desde o primeiro dia como software como dispositivo médico sob EU MDR." },
      ],
    },
    roadmap: {
      eyebrow: "Roadmap Estratégico",
      title: "Destaques do projecto Uetze — 2024 e para lá disso.",
      sub: "Cinco horizontes de engenharia concretos — cada um um marco, não um título.",
      items: [
        { year: "2024",  title: "Fundação",                 desc: "Infraestrutura e Núcleo de IA estabelecidos." },
        { year: "2025",  title: "Validação Piloto",         desc: "Parcerias clínicas, fase de construção do Renalix Experience Center, sistema de energia sustentável." },
        { year: "2026",  title: "Gémeo Digital IA",         desc: "Activação completa do modelo do Gémeo Digital Renal." },
        { year: "2027",  title: "Certificação Médica",      desc: "Conformidade EU MDR alcançada." },
        { year: "2028+", title: "Escalamento Comercial",    desc: "Expansão para o mercado europeu." },
      ],
    },
    closing: {
      eyebrow: "Da Inteligência Digital à Saúde do Dia-a-Dia",
      title: "Uetze não é uma sede — é a infraestrutura fundacional para o cuidado renal preditivo em toda a Europa.",
      ctaReport: "Ler o relatório estratégico",
      ctaBack: "Voltar ao BSGOTECH",
      ctaPdf: "Health Campus (PDF · 18,4 MB)",
      ctaResearch: "Blueprint de Investigação (PDF · 17 MB)",
      legalLine: "NeuNexus Health Intelligence GmbH · Uetze · Baixa Saxónia · Alemanha",
    },
  },
};

const useR = () => {
  const { lang } = React.useContext(LangCtx);
  return DATA[lang] || DATA.en;
};

// -------------------------------------------------------- Layout primitives

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative border-t ${className}`} style={{ background: P.bg, borderColor: P.border }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">{children}</div>
  </section>
);
const Eyebrow = ({ children, color = P.teal }) => (
  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color }}>{children}</div>
);
const H2 = ({ children }) => (
  <h2 className="mt-3 font-semibold tracking-tight text-[26px] lg:text-[36px] leading-[1.1]" style={{ color: "#fff", letterSpacing: "-0.015em" }}>{children}</h2>
);
const Sub = ({ children }) => (
  <p className="mt-4 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>{children}</p>
);

// -------------------------------------------------------- Hero

const Hero = () => {
  const t = useR();
  return (
    <section className="relative overflow-hidden" style={{ background: P.bg }}>
      <div className="absolute inset-0" aria-hidden="true" style={{
        backgroundImage: `radial-gradient(circle at 20% 30%, ${P.teal}18 0%, transparent 50%), radial-gradient(circle at 80% 70%, ${P.rose}12 0%, transparent 50%)`,
      }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-20 lg:pt-32 lg:pb-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em]" style={{ color: P.silver }} data-testid="renalix-back">
          <ChevronRight size={12} className="rotate-180" /> BSGOTECH
        </Link>
        <span className="ml-3 text-[11px] uppercase tracking-[0.24em]" style={{ color: P.silver, opacity: 0.4 }}>·</span>
        <Link to="/reports" className="ml-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.24em]" style={{ color: P.silver }} data-testid="renalix-reports-link">
          <FileText size={11} /> {t.reportsLink}
        </Link>

        <div className="mt-8 flex items-center gap-5 flex-wrap">
          <div className="rounded-xl px-5 py-4 flex items-center" style={{ background: "rgba(255,255,255,0.96)" }}>
            <img src="/renalix/logo-official.png" alt="Renalix" className="h-12 w-auto" data-testid="renalix-hero-logo" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.teal }}>{t.eyebrowDivision}</div>
            <div className="mt-1 text-[13px]" style={{ color: P.silver }}>{t.location}</div>
          </div>
        </div>

        <h1 className="mt-8 font-semibold tracking-tight text-[40px] sm:text-[54px] lg:text-[72px] leading-[0.98] max-w-4xl" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
          {t.heroTitleA}<span style={{ color: P.teal }}>{t.heroTitleB}</span>.
        </h1>
        <p className="mt-6 max-w-3xl text-[18px] lg:text-[22px] font-light leading-snug" style={{ color: P.ink }}>
          {t.heroLead}
        </p>
        <p className="mt-5 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
          {t.heroSub}
        </p>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl">
          {t.pillars.map((m, i) => {
            const Ic = [Building2, Layers, Brain, Workflow][i];
            return (
              <div key={i} data-testid={`pillar-${i}`} className="rounded-lg p-4"
                style={{ background: P.glass, border: `1px solid ${P.border}` }}>
                <Ic size={16} style={{ color: P.teal }} strokeWidth={1.75} />
                <div className="mt-2.5 text-[12.5px] font-semibold" style={{ color: "#fff" }}>{m.title}</div>
                <div className="mt-1 text-[11px]" style={{ color: P.dim }}>{m.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------- Phygital concept

const Phygital = () => {
  const t = useR();
  const icons = [Building2, Cloud, Sparkles];
  return (
    <Section id="phygital">
      <Eyebrow>{t.phygital.eyebrow}</Eyebrow>
      <H2>{t.phygital.title}</H2>
      <Sub>{t.phygital.sub}</Sub>
      <div className="mt-10 grid lg:grid-cols-3 gap-4">
        {t.phygital.items.map((c, i) => {
          const Ic = icons[i];
          return (
            <div key={i} data-testid={`phygital-${i}`}
              className="rounded-lg p-6"
              style={{ background: `linear-gradient(135deg, ${P.teal}0a 0%, ${P.bg2} 60%)`, border: `1px solid ${P.teal}33` }}>
              <div className="w-11 h-11 rounded-md flex items-center justify-center"
                style={{ background: `${P.teal}18`, border: `1px solid ${P.teal}55` }}>
                <Ic size={20} style={{ color: P.teal }} strokeWidth={1.75} />
              </div>
              <div className="mt-4 text-[15px] font-semibold" style={{ color: "#fff" }}>{c.title}</div>
              <div className="mt-1.5 text-[12.5px] leading-relaxed" style={{ color: P.dim }}>{c.desc}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// -------------------------------------------------------- AI Core

const AI_CORE_ICONS = [HeartPulse, LineChart, Activity, Database, Brain, Stethoscope];

const AICore = () => {
  const t = useR();
  return (
    <Section id="ai-core">
      <Eyebrow color={P.rose}>{t.aiCore.eyebrow}</Eyebrow>
      <H2>{t.aiCore.title}</H2>
      <Sub>{t.aiCore.sub}</Sub>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {t.aiCore.items.map((f, i) => {
          const Ic = AI_CORE_ICONS[i];
          return (
            <div key={i} data-testid={`ai-core-${i}`}
              className="rounded-lg p-5 transition hover:border-white/20"
              style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <Ic size={20} style={{ color: P.teal }} strokeWidth={1.6} />
              <div className="mt-3 text-[14px] font-semibold" style={{ color: "#fff" }}>{f.title}</div>
              <div className="mt-1.5 text-[11.5px] leading-relaxed" style={{ color: P.dim }}>{f.desc}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Zones (Renalix Care / Pro)

const Zone = ({ id, num, name, tagline, desc, bullets, color, testid, zoneLabel }) => (
  <div data-testid={testid} className="rounded-xl overflow-hidden"
    style={{ background: `linear-gradient(135deg, ${color}0d 0%, ${P.bg2} 55%)`, border: `1px solid ${color}44` }}>
    <div className="p-8 lg:p-10">
      <div className="text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color }}>{`${zoneLabel} ${num}`}</div>
      <div className="mt-2 text-[22px] font-semibold tracking-tight" style={{ color: "#fff", letterSpacing: "-0.01em" }}>{name}</div>
      <div className="mt-1 text-[13px] uppercase tracking-[0.16em]" style={{ color: P.silver }}>{tagline}</div>
      <p className="mt-4 text-[13.5px] leading-relaxed max-w-xl" style={{ color: P.dim }}>{desc}</p>
      <ul className="mt-5 grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {bullets.map((b, i) => (
          <li key={i} className="text-[12px] leading-relaxed flex gap-2" style={{ color: P.ink }}>
            <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: color }} />
            {b}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const Zones = () => {
  const t = useR();
  return (
    <Section id="zones">
      <Eyebrow color={P.teal}>{t.zones.eyebrow}</Eyebrow>
      <H2>{t.zones.title}</H2>
      <Sub>{t.zones.sub}</Sub>
      <div className="mt-10 grid lg:grid-cols-2 gap-4">
        <Zone testid="zone-care" num="1" color={P.teal} zoneLabel={t.zones.zoneLabel}
          name={t.zones.care.name} tagline={t.zones.care.tagline} desc={t.zones.care.desc} bullets={t.zones.care.bullets} />
        <Zone testid="zone-pro"  num="2" color={P.rose} zoneLabel={t.zones.zoneLabel}
          name={t.zones.pro.name}  tagline={t.zones.pro.tagline}  desc={t.zones.pro.desc}  bullets={t.zones.pro.bullets} />
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Predictive loop

const Loop = () => {
  const t = useR();
  return (
    <Section id="loop">
      <Eyebrow color={P.rose}>{t.loop.eyebrow}</Eyebrow>
      <H2>{t.loop.title}</H2>
      <Sub>{t.loop.sub}</Sub>
      <div className="mt-10 relative">
        <div className="absolute left-[26px] top-4 bottom-4 w-px hidden lg:block"
          style={{ background: `linear-gradient(180deg, ${P.teal}88 0%, ${P.rose}88 100%)` }} aria-hidden="true" />
        <ol className="space-y-4">
          {t.loop.steps.map((s, i) => {
            const c = i <= 1 ? P.teal : i >= 3 ? P.rose : P.silver;
            const step = String(i + 1).padStart(2, "0");
            return (
              <li key={i} className="flex gap-5" data-testid={`loop-${i + 1}`}>
                <div className="shrink-0">
                  <div className="w-[54px] h-[54px] rounded-full flex items-center justify-center relative z-10"
                    style={{ background: "rgba(8,11,18,0.94)", border: `1.5px solid ${c}88` }}>
                    <span className="text-[13px] font-semibold font-mono" style={{ color: c }}>{step}</span>
                  </div>
                </div>
                <div className="flex-1 rounded-md p-4 lg:p-5"
                  style={{ background: `linear-gradient(90deg, ${c}0a 0%, ${P.surface} 60%)`, border: `1px solid ${c}33` }}>
                  <div className="text-[15px] font-semibold tracking-tight" style={{ color: "#fff" }}>{s.title}</div>
                  <div className="mt-1 text-[12.5px] leading-relaxed" style={{ color: P.dim }}>{s.desc}</div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Compliance / infrastructure

const COMPLIANCE_ICONS = [Cloud, Lock, ShieldCheck, CheckCircle2, Workflow, Cpu];

const Compliance = () => {
  const t = useR();
  return (
    <Section id="compliance">
      <Eyebrow color={P.navy}>{t.compliance.eyebrow}</Eyebrow>
      <H2>{t.compliance.title}</H2>
      <Sub>{t.compliance.sub}</Sub>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {t.compliance.items.map((c, i) => {
          const Ic = COMPLIANCE_ICONS[i];
          return (
            <div key={i} data-testid={`compliance-${i}`}
              className="rounded-lg p-5" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <Ic size={20} style={{ color: P.teal }} strokeWidth={1.75} />
              <div className="mt-3 text-[14px] font-semibold" style={{ color: "#fff" }}>{c.title}</div>
              <div className="mt-1.5 text-[11.5px] leading-relaxed" style={{ color: P.dim }}>{c.desc}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Roadmap

const YEAR_ICONS = [Building2, Activity, Brain, ShieldCheck, Sparkles];
const YEAR_COLORS = [P.green, P.amber, P.blue, P.purple, P.cyan];

const Roadmap = () => {
  const t = useR();
  return (
    <Section id="roadmap">
      <Eyebrow color={P.cyan}>{t.roadmap.eyebrow}</Eyebrow>
      <H2>{t.roadmap.title}</H2>
      <Sub>{t.roadmap.sub}</Sub>

      <div className="mt-10 relative">
        <div className="absolute left-[46px] top-4 bottom-4 w-px hidden lg:block"
          style={{ background: `linear-gradient(180deg, ${P.green}88 0%, ${P.amber}88 25%, ${P.blue}88 50%, ${P.purple}88 75%, ${P.cyan}88 100%)` }} aria-hidden="true" />
        <ol className="space-y-4">
          {t.roadmap.items.map((y, i) => {
            const c = YEAR_COLORS[i] || P.silver;
            const Ic = YEAR_ICONS[i] || CheckCircle2;
            return (
              <li key={i} className="flex gap-5" data-testid={`renalix-roadmap-${y.year.replace("+","-plus")}`}>
                <div className="shrink-0 flex items-center gap-3">
                  <div className="hidden lg:block text-[11px] uppercase tracking-[0.24em] font-semibold w-[38px] text-right" style={{ color: c }}>{y.year}</div>
                  <div className="w-[54px] h-[54px] rounded-full flex items-center justify-center"
                    style={{ background: "rgba(8,11,18,0.94)", border: `1.5px solid ${c}88` }}>
                    <Ic size={20} style={{ color: c }} strokeWidth={1.75} />
                  </div>
                </div>
                <div className="flex-1 rounded-md p-4 lg:p-5"
                  style={{ background: `linear-gradient(90deg, ${c}0a 0%, ${P.surface} 60%)`, border: `1px solid ${c}33` }}>
                  <div className="text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: c }}>{y.year}</div>
                  <div className="mt-1 text-[15px] font-semibold tracking-tight" style={{ color: "#fff" }}>{y.title}</div>
                  <div className="mt-1 text-[12.5px] leading-relaxed max-w-2xl" style={{ color: P.dim }}>{y.desc}</div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Closing

const Closing = () => {
  const t = useR();
  return (
    <Section id="closing">
      <div className="rounded-xl p-8 lg:p-12"
        style={{ background: `linear-gradient(135deg, ${P.teal}14 0%, ${P.bg2} 55%, ${P.rose}18 100%)`, border: `1px solid ${P.teal}44` }}>
        <Eyebrow>{t.closing.eyebrow}</Eyebrow>
        <H2>{t.closing.title}</H2>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/report" data-testid="closing-cta-report"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md"
            style={{ background: "#fff", color: "#0A0F1A" }}>
            {t.closing.ctaReport} <ArrowUpRight size={13} />
          </Link>
          <a href="/downloads/NeuNexus_Health_Campus.pdf" target="_blank" rel="noopener noreferrer"
            data-testid="closing-cta-pdf"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition hover:bg-white/5"
            style={{ border: `1px solid ${P.teal}55`, color: P.teal }}>
            <Download size={13} /> {t.closing.ctaPdf}
          </a>
          <a href="/downloads/Predictive_Renal_Healthcare_Blueprint.pdf" target="_blank" rel="noopener noreferrer"
            data-testid="closing-cta-research"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition hover:bg-white/5"
            style={{ border: `1px solid ${P.rose}55`, color: P.rose }}>
            <Download size={13} /> {t.closing.ctaResearch}
          </a>
          <Link to="/" data-testid="closing-cta-bsgo"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition hover:bg-white/5"
            style={{ border: `1px solid ${P.border}`, color: P.ink }}>
            {t.closing.ctaBack} <ChevronRight size={13} />
          </Link>
        </div>
        <div className="mt-10 text-[11px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
          {t.closing.legalLine}
        </div>
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Page

export default function RenalixPage() {
  const t = useR();
  React.useEffect(() => { document.title = t.docTitle; }, [t.docTitle]);
  return (
    <div className="relative" style={{ background: P.bg, color: P.ink }} data-testid="page-renalix">
      <LanguageSwitcher />
      <Hero />
      <Phygital />
      <AICore />
      <Zones />
      <Loop />
      <Compliance />
      <Roadmap />
      <Closing />
    </div>
  );
}
